import { z } from "zod";
import { useContext } from "react";
import { createUser, getUser, saveUser } from "../api/user-controller";
import { ModelDefinition, ValidationMessage, validationMessage } from "../base-components/hooks/useModel";
import { Context } from "../App";
import { RoutePath } from "@/generic/config";

const UserInstanceSchema = z.object({
    id: z.number(),
    instance: z.object({
        id: z.number(),
        identifier: z.string(),
        databaseName: z.string(),
        environment: z.string(),
        active: z.boolean(),
    }),
});

export type UserInstance = z.infer<typeof UserInstanceSchema>;

export const UserSchema = z.object({
    id: z.number(),
    username: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
    phone: z.string(),
    enabled: z.boolean(),
    role: z.string(),
    userInstances: z.array(UserInstanceSchema),
});

export type User = z.infer<typeof UserSchema>;

const ValidationMessages = (model: User) => {
    const { translations } = useContext(Context);
    const rules = translations.model.user.validationRules;
    const messages: ValidationMessage<User>[] = [];

    if (!model.username) {
        messages.push(validationMessage("username", rules.usernameIsRequired, "error"));
    }

    return messages;
};

const emptyUserObject: User = {
    id: 0,
    username: "",
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    enabled: true,
    role: "USER",
    userInstances: [],
};

export const UserModel: ModelDefinition<User> = {
    getById: getUser,
    saveFromModel: saveUser,
    createFromModel: createUser,
    validationMessages: ValidationMessages,
    routePath: RoutePath.Users,
    typeProviderObject: UserSchema.parse(emptyUserObject),
};
